import React, { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import {
  Avatar,
  IconButton,
  CircularProgress,
  Typography,
} from "@mui/material";
import { PhotoCamera } from "@mui/icons-material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { AuthContext, db, storage } from "../../firebase";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { getAncestors } from "../../utils/familyTreeUtils";
import { familyTreeData } from "./family";
import PersonDetailCard from "../../components/PersonDetailCard";
import {
  AncestorTree,
  AncestorTreeCallbacks,
  AncestorTreeUIControls,
} from "@ryandymock/ancestor-tree";
import { FamilyMember } from "../../utils/familyTreeUtils";
import { useAuth } from "../../firebase";
import { useSnackbar } from "notistack";

// Define types that match the package's internal types
interface Person {
  id: string;
  name: string;
  birth?: string;
  death?: string;
  imageUrl?: string;
  spouseId?: string;
  parentIds?: [string?, string?];
}

type PeopleIndex = Record<string, Person>;

// Helper function to convert hierarchical family data to flat PeopleIndex format
const convertFamilyDataToPeopleIndex = (
  familyData: FamilyMember[]
): PeopleIndex => {
  const people: PeopleIndex = {};

  const visited = new Set<string>();

  const traverse = (node: FamilyMember) => {
    if (visited.has(node.id)) return; // avoid redundant processing
    visited.add(node.id);

    // In our data structure, the node's "children" array actually stores its parents
    const parentIds: [string | undefined, string | undefined] = [
      undefined,
      undefined,
    ];
    if (node.children) {
      if (node.children[0]) parentIds[0] = node.children[0].id;
      if (node.children[1]) parentIds[1] = node.children[1].id;
    }

    people[node.id] = {
      id: node.id,
      name: node.name,
      birth: node.birthdate || "",
      death: node.deathdate || "",
      parentIds: parentIds[0] || parentIds[1] ? parentIds : undefined,
      spouseId: (node as any).spouseId as string | undefined,
    };

    // Recurse upward to parents
    if (node.children) {
      node.children.forEach((parent) => traverse(parent));
    }
  };

  familyData.forEach((root) => traverse(root));

  // Second pass: infer spouseId when missing but partner references current id
  Object.values(people).forEach((person) => {
    if (!person.spouseId) {
      const partner = Object.values(people).find(
        (p) => p.spouseId === person.id
      );
      if (partner) {
        person.spouseId = partner.id;
      }
    }
  });

  return people;
};

const findFamilyMember = (
  memberId: string,
  familyData: FamilyMember[]
): FamilyMember | null => {
  for (const member of familyData) {
    if (member.id === memberId) {
      return member;
    }
    if (member.children) {
      const found = findFamilyMember(memberId, member.children);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const FamilyTreeTab: React.FC = () => {
  const [familyTree, setFamilyTree] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const { user } = useAuth();

  // Convert family tree data to the format expected by AncestorTree
  const peopleIndex = convertFamilyDataToPeopleIndex(familyTree);

  // Define callbacks for AncestorTree interactions
  const callbacks: AncestorTreeCallbacks = {
    onPersonClick: (person: Person) => {
      console.log("Person clicked:", person);
      setSelectedPersonId(person.id);
    },
    onViewportChange: (x: number, y: number, zoom: number) => {
      console.log("Viewport changed:", { x, y, zoom });
    },
    onTreePan: (x: number, y: number) => {
      console.log("Tree panned:", { x, y });
    },
    onTreeZoom: (zoom: number) => {
      console.log("Tree zoomed:", zoom);
    },
    onCoupleExpansion: (
      partner1: Person | undefined,
      partner2: Person | undefined,
      isExpanded: boolean
    ) => {
      console.log("Couple expansion:", { partner1, partner2, isExpanded });
    },
  };

  // Helper function to create template-based subtitle formatter
  const createSubtitleFormatter = (template: string) => {
    return (person: Person) => {
      // Helper function to format dates
      const formatDate = (dateString?: string, format?: string) => {
        if (
          !dateString ||
          dateString === "YYYY-MM-DD" ||
          dateString.trim() === ""
        )
          return "";

        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "";

          // Simple date formatting - you can expand this based on needs
          if (format === "yyyy") {
            return date.getFullYear().toString();
          }
          // Add more format options as needed
          return date.getFullYear().toString();
        } catch {
          return "";
        }
      };

      // Calculate lifespan
      const getLifespan = (person: Person) => {
        const birthYear = person.birth ? formatDate(person.birth, "yyyy") : "";
        const deathYear = person.death ? formatDate(person.death, "yyyy") : "";

        if (birthYear && deathYear) {
          return `${birthYear}-${deathYear}`;
        } else if (birthYear) {
          return `${birthYear}-`;
        }
        return "";
      };

      // Replace template variables
      return template
        .replace(/{name}/g, person.name || "")
        .replace(/{birth}/g, person.birth || "")
        .replace(/{death}/g, person.death || "")
        .replace(/{id}/g, person.id || "")
        .replace(
          /{birthYear}/g,
          person.birth ? formatDate(person.birth, "yyyy") : ""
        )
        .replace(
          /{deathYear}/g,
          person.death ? formatDate(person.death, "yyyy") : ""
        )
        .replace(/{lifespan}/g, getLifespan(person));
    };
  };

  // Configure UI controls for AncestorTree
  const uiControls: AncestorTreeUIControls = {
    showControls: true,
    showMiniMap: false,
    showBackground: true,
    enablePan: true,
    enableZoom: true,
    enableFitView: true,
    backgroundColor: "#fafafa",
    // Custom spacing to fix MUI theme card overlapping
    nodeHeight: 140,
    verticalGaps: [0, 450, 200, 400, 150],
    defaultVerticalGap: 75,
    // Custom subtitle formatting to show lifespan
    formatPersonSubtitle: createSubtitleFormatter("{lifespan}"),
  };

  // useEffect to track familyTree state variable changes
  useEffect(() => {
    console.log("Family tree updated:", familyTree);
  }, [familyTree]);

  useEffect(() => {
    if (!user) {
      console.log("User not authenticated");
      return;
    }
    console.log("User authenticated:", user.uid);
    const userDocRef = doc(db, "users", user.uid);

    setLoading(true);

    const unsubscribe = onSnapshot(
      userDocRef,
      async (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          // If the field is completely absent or falsy, write the default array.
          if (!Array.isArray(data.familyTreeData)) {
            // Only initialize with default data if we haven't already done so
            if (!hasInitialized) {
              const initial: FamilyMember[] = familyTreeData; // or use []
              console.log(
                "Initializing familyTreeData with default data (one-time only)"
              );
              await setDoc(
                userDocRef,
                { familyTreeData: initial },
                { merge: true }
              );
              setHasInitialized(true);
            }
            setFamilyTree(familyTreeData);
            if (!selectedPersonId && familyTreeData.length > 0) {
              setSelectedPersonId(familyTreeData[0].id);
            }
          } else {
            // Load existing data from Firestore
            const currentSelectedId = selectedPersonId;
            setFamilyTree(data.familyTreeData);
            // Only set initial selection if no one is currently selected AND we're not in the middle of an update
            if (
              !currentSelectedId &&
              !saving &&
              data.familyTreeData.length > 0
            ) {
              setSelectedPersonId(data.familyTreeData[0].id);
            }
          }
        } else {
          // Document doesn't exist at all: create it with a default array
          console.log(
            "BLOCKED FIRESTORE WRITE: Would create user doc with default familyTreeData"
          );
          const initial: FamilyMember[] = familyTreeData; // or use []
          console.log("Default data that would be written:", initial);
          // FIRESTORE WRITES DISABLED TO PREVENT LOOP
          // await setDoc(
          //   userDocRef,
          //   { familyTreeData: initial },
          //   { merge: true }
          // );
          setFamilyTree(initial);
          if (!selectedPersonId && initial.length > 0) {
            setSelectedPersonId(initial[0].id);
          }
        }
        setLoading(false);
      },
      (error) => {
        console.error("Snapshot error:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]); // Only depend on user, not selectedPersonId

  // Recursive function to update the family member in the tree
  const updateFamilyMember = (
    members: FamilyMember[],
    id: string,
    updatedData: Partial<FamilyMember>
  ): FamilyMember[] => {
    return members.map((member) => {
      if (member.id === id) {
        return { ...member, ...updatedData };
      }
      if (member.children) {
        return {
          ...member,
          children: updateFamilyMember(member.children, id, updatedData),
        };
      }
      return member;
    });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", padding: 2, gap: 2 }}>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <div style={{ width: "100%", height: "600px" }}>
            <AncestorTree
              people={peopleIndex}
              rootId={
                selectedPersonId ||
                (familyTree.length > 0 ? familyTree[0].id : "self")
              }
              callbacks={callbacks}
              uiControls={uiControls}
            />
          </div>
        </Box>
        <Box sx={{ width: 600 }}>
          {loading ? (
            <CircularProgress />
          ) : (
            selectedPersonId && (
              <PersonDetailCard
                member={findFamilyMember(selectedPersonId, familyTree)!}
                onChange={async (updated) => {
                  try {
                    setSaving(true);

                    // Update the family tree with the new data
                    const updatedTree = updateFamilyMember(
                      familyTree,
                      updated.id,
                      updated
                    );

                    // Update local state immediately for responsive UI
                    setFamilyTree(updatedTree);

                    // BLOCKED: Save to Firestore (this will trigger onSnapshot but selection will be preserved)
                    console.log(
                      "BLOCKED FIRESTORE WRITE: Would save updated family tree data"
                    );
                    console.log(
                      "Updated tree that would be written:",
                      updatedTree
                    );
                    // const userDocRef = doc(db, "users", userId);
                    // await setDoc(
                    //   userDocRef,
                    //   { familyTreeData: updatedTree },
                    //   { merge: true }
                    // );
                  } finally {
                    setSaving(false);
                  }
                }}
              />
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FamilyTreeTab;
